/**
 * Table sub-components for the Import Dashboard.
 *
 * Extracted from ImportDashboard.tsx to keep files under 500 lines.
 */
import { Fragment, useMemo, useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Pagination } from '../../components/ui/pagination';
import { ChevronDown, ChevronRight, Layers, Search } from 'lucide-react';
import type { ImportJob } from '../../lib/api/imports';

// ---------------------------------------------------------------------------
// Shared helpers (duplicated from parent — kept minimal to avoid circular deps)
// ---------------------------------------------------------------------------

const STATUS_CONFIG: Record<string, { color: string; bg: string }> = {
  completed: { color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200' },
  failed: { color: 'text-red-700', bg: 'bg-red-50 border-red-200' },
  partial: { color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200' },
  running: { color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200' },
  pending: { color: 'text-slate-700', bg: 'bg-slate-50 border-slate-200' },
  cancelled: { color: 'text-gray-500', bg: 'bg-gray-50 border-gray-200' },
};

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG['pending'];
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${cfg.bg} ${cfg.color}`}>
      {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
    </span>
  );
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) return '-';
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}m ${secs}s`;
}

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return '-';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ---------------------------------------------------------------------------
// Jobs Table
// ---------------------------------------------------------------------------

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'partial', label: 'Partial' },
  { value: 'running', label: 'Running' },
  { value: 'pending', label: 'Pending' },
  { value: 'cancelled', label: 'Cancelled' },
];

/**
 * Group consecutive jobs that belong to one push run. The queue adapter
 * stamps every job that consumed frames of the same marker-bracketed push
 * with the same push-derived correlation_id — that shared id is the ONLY
 * grouping signal. Jobs from before the stamping (unique correlation ids)
 * simply stay ungrouped; no time heuristics, so two separate back-to-back
 * runs can never merge.
 */
function groupJobs(jobs: ImportJob[]): ImportJob[][] {
  // Bucket strictly by correlation_id, NOT by adjacency: two pushes
  // draining in parallel interleave their jobs (parts, grns, parts, …),
  // and an adjacency-run grouper breaks at every alternation. Unstamped
  // jobs carry a random per-job id, so they naturally stay solo rows.
  // Groups keep the position of their newest (first-seen) job.
  const groups: ImportJob[][] = [];
  const byCorrelation = new Map<string, ImportJob[]>();
  for (const job of jobs) {
    const key = job.correlation_id;
    const existing = key ? byCorrelation.get(key) : undefined;
    if (existing) {
      existing.push(job);
      continue;
    }
    const group: ImportJob[] = [job];
    groups.push(group);
    if (key) byCorrelation.set(key, group);
  }
  return groups;
}

function rollupStatus(group: ImportJob[]): string {
  if (group.some(j => j.status === 'failed')) return 'failed';
  if (group.some(j => j.status === 'running' || j.status === 'pending')) return 'running';
  if (group.some(j => j.status === 'partial')) return 'partial';
  return group[0]?.status ?? 'completed';
}

function sum(group: ImportJob[], f: (j: ImportJob) => number): number {
  return group.reduce((acc, j) => acc + (f(j) || 0), 0);
}

/**
 * CR-8186: Recent Jobs pages over pushes (group=1), and that path serves at
 * most 100 groups a page (GROUP_MAX_PAGE_SIZE in import_service/views), so
 * it offers only the sizes it serves in full instead of the shared 25-2000.
 * A backend test fails if the largest size here and that cap disagree.
 */
const JOB_GROUP_PAGE_SIZES = [25, 50, 100];

interface JobsTableProps {
  jobs: ImportJob[];
  onSelectJob: (jobId: string) => void;
  currentPage: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  search: string;
  onSearchChange: (search: string) => void;
  statusFilter: string;
  onStatusFilterChange: (status: string) => void;
}

export function JobsTable({
  jobs,
  onSelectJob,
  currentPage,
  totalCount,
  pageSize,
  onPageChange,
  onPageSizeChange,
  search,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
}: JobsTableProps) {
  const totalPages = Math.ceil(totalCount / pageSize) || 1;
  const groups = useMemo(() => groupJobs(jobs), [jobs]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggleGroup = (key: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const jobCells = (job: ImportJob) => (
    <>
      <td className="p-3 text-right tabular-nums">{job.records_processed.toLocaleString()}</td>
      <td className="p-3 text-right tabular-nums text-emerald-600">{job.records_created.toLocaleString()}</td>
      <td className="p-3 text-right tabular-nums">
        {job.records_updated > 0 ? (
          <span className="text-blue-600">{job.records_updated.toLocaleString()}</span>
        ) : (
          <span className="text-muted-foreground">0</span>
        )}
      </td>
      <td className="p-3 text-right tabular-nums">
        {job.records_skipped > 0 ? (
          <span className="text-amber-600">{job.records_skipped.toLocaleString()}</span>
        ) : (
          <span className="text-muted-foreground">0</span>
        )}
      </td>
      <td className="p-3 text-right tabular-nums">
        {job.records_failed > 0 ? (
          <span className="text-red-600">{job.records_failed.toLocaleString()}</span>
        ) : (
          <span className="text-muted-foreground">0</span>
        )}
      </td>
      <td className="p-3 text-right tabular-nums text-muted-foreground">{formatDuration(job.duration_seconds)}</td>
      <td className="p-3 text-muted-foreground whitespace-nowrap">{timeAgo(job.created_at)}</td>
      <td className="p-3"><ChevronRight className="h-4 w-4 text-muted-foreground" /></td>
    </>
  );

  return (
    <Card>
      {/* Search & filter bar */}
      <div className="flex items-center gap-3 p-4 border-b">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by job ID, data type, customer, or error..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => onStatusFilterChange(e.target.value)}
          className="px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
        >
          {STATUS_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {jobs.length === 0 ? (
        <CardContent className="py-12 text-center text-muted-foreground">
          {search || statusFilter ? 'No jobs match your filters' : 'No import jobs found'}
        </CardContent>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-3 font-medium">Status</th>
                  <th className="text-left p-3 font-medium">Data Type</th>
                  <th className="text-left p-3 font-medium">Customer</th>
                  <th className="text-left p-3 font-medium">Source</th>
                  <th className="text-right p-3 font-medium">Total</th>
                  <th className="text-right p-3 font-medium">Created</th>
                  <th className="text-right p-3 font-medium">Updated</th>
                  <th className="text-right p-3 font-medium">Skipped</th>
                  <th className="text-right p-3 font-medium">Failed</th>
                  <th className="text-right p-3 font-medium">Duration</th>
                  <th className="text-left p-3 font-medium">When</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {groups.map(group => {
                  const first = group[0];
                  if (group.length === 1) {
                    return (
                      <tr key={first.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors cursor-pointer" onClick={() => onSelectJob(first.job_id)}>
                        <td className="p-3"><StatusBadge status={first.status} /></td>
                        <td className="p-3 font-medium">{first.data_type_display}</td>
                        <td className="p-3 text-muted-foreground">{first.customer_name}</td>
                        <td className="p-3 text-muted-foreground">{first.source_display}</td>
                        {jobCells(first)}
                      </tr>
                    );
                  }
                  // Keyed by the push, not its newest job: while a push is still
                  // landing every refresh brings a newer first job, and an open
                  // group snapped shut.
                  const key = first.correlation_id ? `push-${first.correlation_id}` : String(first.id);
                  const open = expanded.has(key);
                  const totalDuration = sum(group, j => j.duration_seconds ?? 0);
                  return (
                    <Fragment key={key}>
                      <tr
                        className="border-b hover:bg-muted/40 transition-colors cursor-pointer bg-muted/20"
                        onClick={() => toggleGroup(key)}
                      >
                        <td className="p-3"><StatusBadge status={rollupStatus(group)} /></td>
                        <td className="p-3 font-medium">
                          <span className="flex items-center gap-1.5">
                            {open
                              ? <ChevronDown className="h-4 w-4 text-muted-foreground" />
                              : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                            <Layers className="h-3.5 w-3.5 text-muted-foreground" />
                            {first.data_type_display}
                            <span className="text-xs font-normal text-muted-foreground">· {group.length} jobs</span>
                          </span>
                        </td>
                        <td className="p-3 text-muted-foreground">{first.customer_name}</td>
                        <td className="p-3 text-muted-foreground">{first.source_display}</td>
                        <td className="p-3 text-right tabular-nums font-medium">{sum(group, j => j.records_processed).toLocaleString()}</td>
                        <td className="p-3 text-right tabular-nums text-emerald-600">{sum(group, j => j.records_created).toLocaleString()}</td>
                        <td className="p-3 text-right tabular-nums text-blue-600">{sum(group, j => j.records_updated).toLocaleString()}</td>
                        <td className="p-3 text-right tabular-nums text-amber-600">{sum(group, j => j.records_skipped).toLocaleString()}</td>
                        <td className="p-3 text-right tabular-nums">
                          {sum(group, j => j.records_failed) > 0
                            ? <span className="text-red-600">{sum(group, j => j.records_failed).toLocaleString()}</span>
                            : <span className="text-muted-foreground">0</span>}
                        </td>
                        <td className="p-3 text-right tabular-nums text-muted-foreground">{formatDuration(totalDuration || null)}</td>
                        <td className="p-3 text-muted-foreground whitespace-nowrap">{timeAgo(first.created_at)}</td>
                        <td className="p-3" />
                      </tr>
                      {open && group.map(job => (
                        <tr
                          key={job.id}
                          className="border-b last:border-0 hover:bg-muted/30 transition-colors cursor-pointer"
                          onClick={() => onSelectJob(job.job_id)}
                        >
                          <td className="p-3 pl-8"><StatusBadge status={job.status} /></td>
                          <td className="p-3 pl-10 text-muted-foreground">{job.data_type_display}</td>
                          <td className="p-3 text-muted-foreground">{job.customer_name}</td>
                          <td className="p-3 text-muted-foreground">{job.source_display}</td>
                          {jobCells(job)}
                        </tr>
                      ))}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 pb-2">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              itemsPerPage={pageSize}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
              pageSizeOptions={JOB_GROUP_PAGE_SIZES}
              itemLabel="pushes"
            />
          </div>
        </>
      )}
    </Card>
  );
}
